import { InjectionToken, Provider } from '@angular/core';
import {
  IRynReplayEventSource,
  RYN_REPLAY_EVENT_SOURCE,
  RynReplayHttpEventSource,
} from './ryn-replay-event-source';
import {
  IRynReplaySessionSource,
  RYN_REPLAY_SESSION_SOURCE,
  RynReplayHttpSessionSource,
} from './ryn-replay-session-source';
import {
  IRynReplayBackendTelemetrySource,
  RYN_REPLAY_BACKEND_TELEMETRY_SOURCE,
  RynReplayHttpBackendTelemetrySource,
} from './ryn-replay-backend-source';
import { RynReplayAccessToken } from './ryn-replay-access-token';
import {
  IRynReplayIssueSource,
  RYN_REPLAY_ISSUE_SOURCE,
  RynReplayHttpIssueSource,
} from './ryn-replay-issue-source';

/**
 * How the host app hands over the viewer's bearer token.
 *
 * A function rather than a service, because the apps in this monorepo do not
 * agree on where a token comes from. Auth0 apps call `getAccessTokenSilently`,
 * Entra apps go through MSAL, and injecting either one here would make this lib
 * unusable by half the workspace.
 *
 * Resolve a `RynReplayAccessToken`: either `{ token }`, or a `{ reason }`
 * naming why there is no token (no-session / wrong-issuer / acquire-failed).
 * The read sources word their refusal from the reason, so an Auth0-signed-in
 * viewer is told to use their corporate sign in rather than that they are "not
 * signed in" (#30126). The refusal is a message on screen, not a crash.
 */
export type RynReplayTokenFactory = () => Promise<RynReplayAccessToken>;

export const RYN_REPLAY_TOKEN_FACTORY =
  new InjectionToken<RynReplayTokenFactory>('RYN_REPLAY_TOKEN_FACTORY');

/**
 * Wires the player and the session list to the read API.
 *
 * Until an app adds this, both surfaces inject null and say so on screen. That
 * is the state every app was in when this was written: the read API existed and
 * was tested server-side, but nothing on the client ever constructed a source,
 * so the only way to view a recording was to open an export file by hand. The
 * failure was invisible because both injections are `@Optional()`, which is
 * correct behaviour and also why nobody noticed.
 *
 * Both sources are provided together on purpose. An app that can list sessions
 * but cannot play one, or the reverse, is a worse experience than an app that
 * honestly has neither, and splitting them invites exactly that half-wired
 * state.
 *
 * ONE THING TO KNOW BEFORE YOU CALL THIS: the read API validates an ENTRA token.
 * So what happens on an Auth0 session depends entirely on what YOUR token
 * factory returns, and the two outcomes look nothing alike:
 *
 *   - Return the Auth0 token anyway and every request comes back 401, because
 *     the token is real and minted by the wrong issuer.
 *   - Return undefined and NO REQUEST IS MADE AT ALL. The sources short circuit
 *     and render their unauthenticated state.
 *
 * web-software-security does the second (see createReplayTokenFactory), and that
 * is the pattern to copy. Verified in a browser 2026-07-25: signed in via the
 * Auth0 branch, the read API received zero requests.
 *
 * Check what your app's token factory actually returns before assuming this is
 * enough. A factory that yields no token now returns a reason
 * (no-session / wrong-issuer / acquire-failed), so the surfaces word the
 * refusal for the real state instead of claiming "not signed in" for a viewer
 * signed in with the wrong issuer (#30126, resolved).
 */
export const provideRynReplayReadSources = (): Provider[] => [
  {
    provide: RYN_REPLAY_EVENT_SOURCE,
    useFactory: (token: RynReplayTokenFactory): IRynReplayEventSource =>
      new RynReplayHttpEventSource(token),
    deps: [RYN_REPLAY_TOKEN_FACTORY],
  },
  {
    provide: RYN_REPLAY_SESSION_SOURCE,
    useFactory: (token: RynReplayTokenFactory): IRynReplaySessionSource =>
      new RynReplayHttpSessionSource(token),
    deps: [RYN_REPLAY_TOKEN_FACTORY],
  },
  {
    provide: RYN_REPLAY_BACKEND_TELEMETRY_SOURCE,
    useFactory: (
      token: RynReplayTokenFactory
    ): IRynReplayBackendTelemetrySource =>
      new RynReplayHttpBackendTelemetrySource(token),
    deps: [RYN_REPLAY_TOKEN_FACTORY],
  },
  {
    provide: RYN_REPLAY_ISSUE_SOURCE,
    useFactory: (token: RynReplayTokenFactory): IRynReplayIssueSource =>
      new RynReplayHttpIssueSource(token),
    deps: [RYN_REPLAY_TOKEN_FACTORY],
  },
];

/**
 * Starts a corporate (Entra) sign-in from the session list's wrong-issuer
 * refusal (#31381).
 *
 * Optional and host-provided for the same reason the token factory is: the two
 * hosts that can sign a viewer into Entra do it differently (web-ryn-replay
 * through MSAL directly, web-software-security through its dual-login Entra
 * branch), and neither belongs in a shared library. An app that cannot sign a
 * viewer into Entra, web-safety today, simply does not provide this, and the
 * refusal keeps its honest wording with no dead button.
 */
export type RynReplaySignIn = () => void;

export const RYN_REPLAY_SIGN_IN = new InjectionToken<RynReplaySignIn>(
  'RYN_REPLAY_SIGN_IN'
);

/**
 * The minimal slice of MSAL this needs. Both hosts hold a full `MsalService`;
 * narrowing it to this keeps the factory (and its unit test) free of MSAL.
 */
export interface RynReplayEntraLogin {
  loginRedirect(request: { scopes: string[]; prompt?: string }): unknown;
}

/**
 * Builds the sign-in action both Entra hosts wire to `RYN_REPLAY_SIGN_IN`.
 *
 * A wrong-issuer viewer is ALREADY signed in with the wrong account, so the
 * redirect asks for the account picker (`select_account`) rather than silently
 * reusing that cached session, and requests the read API audience so the token
 * it returns can actually read replays. A missing audience yields no scope
 * rather than the unusable string `undefined/.default`.
 */
export const createRynReplayEntraSignIn = (
  msal: RynReplayEntraLogin,
  audience?: string
): RynReplaySignIn => {
  const scopes = audience ? [`${audience}/.default`] : [];
  return () => {
    msal.loginRedirect({ scopes, prompt: 'select_account' });
  };
};
